import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { Copy, MessageCircle, Share2 } from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { z } from "zod";

const profileSchema = z.object({
  name: z.string().min(1, "Name is required"),
  username: z.string().min(1, "Username is required"),
});

const paymentSchema = z.object({
  amount: z
    .union([z.string(), z.number()])
    .transform((val) => (typeof val === "string" ? parseFloat(val) : val))
    .refine((val) => val > 0, { message: "Amount must be positive" }),
  note: z.string().min(1, "Note is required"),
});

interface ProfileData {
  name: string;
  username: string;
}

interface PaymentData {
  amount: number;
  note: string;
}

interface LinkGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
}

const appThemes = {
  venmo: {
    bgColor: "bg-[#3D95CE]",
    textColor: "text-white",
    secondaryColor: "text-blue-100",
    logo: "/venmo.png",
    name: "Venmo",
    prefix: "@",
  },
  paypal: {
    bgColor: "bg-[#0070E0]",
    textColor: "text-white",
    secondaryColor: "text-blue-100",
    logo: "/paypal.png",
    name: "PayPal",
    prefix: "paypal.me/",
  },
  cashapp: {
    bgColor: "bg-[#00D632]",
    textColor: "text-white",
    secondaryColor: "text-green-100",
    logo: "/cashapp.png",
    name: "Cash App",
    prefix: "$",
  },
};

type AppType = keyof typeof appThemes;
const availableApps: AppType[] = ["venmo", "cashapp", "paypal"];

// Local storage utilities
const PROFILE_STORAGE_KEY = "dollarback-profile";
const LINKS_STORAGE_KEY = "dollarback-links";

interface SavedLink {
  id: string;
  name: string;
  username: string;
  amount: number;
  note: string;
  provider: string;
  link: string;
  createdAt: string;
}

const saveProfileToStorage = (profile: ProfileData) => {
  localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(profile));
};

const getProfileFromStorage = (): ProfileData | null => {
  try {
    const stored = localStorage.getItem(PROFILE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveLinkToStorage = (linkData: Omit<SavedLink, 'id' | 'createdAt'>) => {
  try {
    const existing = getLinksFromStorage();
    const newLink: SavedLink = {
      ...linkData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    };
    
    // Keep only the last 10 links
    const updated = [newLink, ...existing].slice(0, 10);
    localStorage.setItem(LINKS_STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error("Failed to save link:", error);
  }
};

const getLinksFromStorage = (): SavedLink[] => {
  try {
    const stored = localStorage.getItem(LINKS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export function PaymentLinkBuilder({ isOpen, onClose }: LinkGeneratorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedApp, setSelectedApp] = useState<AppType | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [savedProfile, setSavedProfile] = useState<ProfileData | null>(null);

  const profileForm = useForm<ProfileData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: "",
      username: "",
    },
    mode: "onChange",
  });

  const paymentForm = useForm<PaymentData>({
    resolver: zodResolver(paymentSchema),
    defaultValues: {
      amount: undefined,
      note: "",
    },
    mode: "onChange",
  });

  const watchName = profileForm.watch("name");
  const watchUsername = profileForm.watch("username");
  const watchAmount = paymentForm.watch("amount");
  const watchNote = paymentForm.watch("note");

  useEffect(() => {
    if (isOpen) {
      const saved = getProfileFromStorage();
      setSavedProfile(saved);

      if (saved) {
        // User has saved profile, start at step 2
        profileForm.reset(saved);
        setStep(2);
        setSelectedApp("venmo"); // Default to Venmo
      } else {
        // New user, start at step 1
        profileForm.reset({ name: "", username: "" });
        setStep(1);
      }

      paymentForm.reset({ amount: undefined, note: "" });
      setGeneratedLink(null);
      setIsLoading(false);
    }
  }, [isOpen, profileForm, paymentForm]);

  const handleAppSelect = (app: AppType) => {
    setSelectedApp(app);
    setStep(2);
  };

  const onProfileSubmit = (data: ProfileData) => {
    saveProfileToStorage(data);
    setSavedProfile(data);
    setStep(2);
    setSelectedApp("venmo"); // Default to Venmo
    toast.success("Profile saved! You won't need to enter this again.");
  };

  const onPaymentSubmit = async (data: PaymentData) => {
    if (!selectedApp) return;

    setIsLoading(true);
    setGeneratedLink(null);

    try {
      if (!savedProfile) {
        toast.error(
          "Profile not found. Please go back and enter your details."
        );
        return;
      }

      // Construct the dollarback.cash URL
      const params = new URLSearchParams({
        name: savedProfile.name,
        username: savedProfile.username,
        amount: data.amount.toFixed(2), // Ensure amount has two decimal places
        note: data.note,
        provider: selectedApp, // Add provider to query params
      });

      const link = `${window.location.origin}/pay?${params.toString()}`;

      setGeneratedLink(link);

      // Copy to clipboard automatically
      navigator.clipboard
        .writeText(link)
        .then(() => {
          toast.success("Payment link copied to clipboard!");
        })
        .catch((err) => {
          console.error("Could not copy link: ", err);
          toast.error("Failed to copy link to clipboard");
        });

      setStep(3); // Move to step 3: Show Link
    } catch (error) {
      console.error("Error generating link:", error);
      toast.error(
        "Failed to generate link. Please check your inputs and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const theme = selectedApp ? appThemes[selectedApp] : null;

  const formatCurrency = (amount: string | number | undefined) => {
    if (amount === undefined || amount === null || amount === "")
      return "$0.00";
    const value = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(value)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const handleClose = () => {
    profileForm.reset();
    paymentForm.reset();
    setSelectedApp(null);
    setStep(1);
    setGeneratedLink(null);
    setIsLoading(false);
    onClose();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 text-center pt-16 sm:pt-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:w-full sm:max-w-xl sm:p-6 max-h-[80vh] overflow-y-auto">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div>
                  <div className="text-center sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Create a DollarBack Link
                    </Dialog.Title>
                  </div>

                  <div className="mt-2">
                    {step === 1 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-500 mb-4">
                          First, let's set up your profile. This information
                          will be saved so you don't have to enter it again.
                        </p>
                        <form
                          onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                        >
                          <div className="space-y-4">
                            <div>
                              <label
                                htmlFor="name"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Your Name
                              </label>
                              <input
                                id="name"
                                type="text"
                                {...profileForm.register("name")}
                                className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="John Doe"
                              />
                              {profileForm.formState.errors.name && (
                                <p className="mt-1 text-sm text-red-600">
                                  {profileForm.formState.errors.name.message?.toString()}
                                </p>
                              )}
                            </div>

                            <div>
                              <label
                                htmlFor="username"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                <div className="flex items-center">
                                  <img
                                    src="/venmo.png"
                                    alt="Venmo"
                                    width={16}
                                    height={16}
                                    className="h-4 w-4 mr-2 rounded"
                                  />
                                  Venmo Username
                                </div>
                              </label>
                              <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                  <span className="text-gray-500 sm:text-sm">
                                    @
                                  </span>
                                </div>
                                <input
                                  id="username"
                                  type="text"
                                  {...profileForm.register("username")}
                                  className="block w-full rounded-md border-0 py-2 pl-8 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                  placeholder="johndoe"
                                />
                              </div>
                              {profileForm.formState.errors.username && (
                                <p className="mt-1 text-sm text-red-600">
                                  {profileForm.formState.errors.username.message?.toString()}
                                </p>
                              )}
                              <p className="mt-1 text-xs text-gray-500">
                                Enter your Venmo username for payments to be
                                sent to you.
                              </p>
                            </div>

                            <button
                              type="submit"
                              className="w-full inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 bg-indigo-600 hover:bg-indigo-500"
                            >
                              Save Profile & Continue
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : step === 2 ? (
                      <div>
                        <form
                          onSubmit={paymentForm.handleSubmit(onPaymentSubmit)}
                        >
                          <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                            {savedProfile ? (
                              <div className="space-y-5">
                                {/* Profile Display */}
                                <div className="bg-white p-4 rounded-lg border border-gray-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-sm font-medium text-gray-900">
                                      Your Profile
                                    </h3>
                                    <button
                                      type="button"
                                      onClick={() => setStep(1)}
                                      className="text-sm text-indigo-600 hover:text-indigo-500"
                                    >
                                      Edit
                                    </button>
                                  </div>
                                  <div className="space-y-2">
                                    <div className="flex items-center">
                                      <img
                                        src="/venmo.png"
                                        alt="Venmo"
                                        width={16}
                                        height={16}
                                        className="h-4 w-4 mr-2 rounded"
                                      />
                                      <span className="text-sm text-gray-600">
                                        <span className="font-medium">
                                          {savedProfile.name}
                                        </span>{" "}
                                        (@{savedProfile.username})
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                <div className="space-y-4">
                                  <div>
                                    <label
                                      htmlFor="amount"
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      Amount
                                    </label>
                                    <div className="relative">
                                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                        <span className="text-gray-500 sm:text-sm">
                                          $
                                        </span>
                                      </div>
                                      <input
                                        id="amount"
                                        type="number"
                                        step="0.01"
                                        {...paymentForm.register("amount")}
                                        className="block w-full rounded-md border-0 py-2 pl-7 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                        placeholder="0.00"
                                      />
                                    </div>
                                    {paymentForm.formState.errors.amount && (
                                      <p className="mt-1 text-sm text-red-600">
                                        {paymentForm.formState.errors.amount.message?.toString()}
                                      </p>
                                    )}
                                  </div>

                                  <div>
                                    <label
                                      htmlFor="note"
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      Note
                                    </label>
                                    <input
                                      id="note"
                                      type="text"
                                      {...paymentForm.register("note")}
                                      className="block w-full rounded-md border-0 py-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                      placeholder="VIP Tickets"
                                    />
                                    {paymentForm.formState.errors.note && (
                                      <p className="mt-1 text-sm text-red-600">
                                        {paymentForm.formState.errors.note.message?.toString()}
                                      </p>
                                    )}
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <button
                                    type="submit"
                                    disabled={
                                      isLoading || !watchAmount || !watchNote
                                    }
                                    className="w-full inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-indigo-600 hover:bg-indigo-500"
                                  >
                                    <img
                                      src="/venmo.png"
                                      alt="Venmo"
                                      width={16}
                                      height={16}
                                      className="h-4 w-4 mr-2 rounded"
                                    />
                                    {isLoading
                                      ? "Generating..."
                                      : "Generate Payment Link"}
                                  </button>
                                </div>

                                <div className="mt-5 mb-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                                    Preview of your message:
                                  </h4>

                                  {/* Chat-style preview */}
                                  <div className="max-w-xs">
                                    <div
                                      className="rounded-2xl bg-indigo-600 p-4 shadow-md text-white relative"
                                      style={{ maxWidth: "300px" }}
                                    >
                                      <div className="absolute top-4 right-4 bg-white/20 rounded-full w-9 h-9 flex items-center justify-center">
                                        <span className="text-white text-lg font-bold">
                                          $
                                        </span>
                                      </div>

                                      <div className="pr-12">
                                        {" "}
                                        {/* Add right padding to prevent overlap with logo */}
                                        <p className="text-sm opacity-80 break-words">
                                          Pay{" "}
                                          {savedProfile?.name || "{Your Name}"}{" "}
                                          {formatCurrency(watchAmount || 0)} for
                                          <br />"{watchNote || "{Note}"}"
                                        </p>
                                        <p className="text-xs opacity-80 mt-2 mb-1">
                                          dollarback.cash
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-6">
                                <p className="text-sm text-gray-500 mb-4">
                                  No profile found. Please set up your profile
                                  first.
                                </p>
                                <button
                                  type="button"
                                  onClick={() => setStep(1)}
                                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                >
                                  Set Up Profile
                                </button>
                              </div>
                            )}
                          </div>
                        </form>
                      </div>
                    ) : step === 3 ? (
                      <div>
                        {generatedLink ? (
                          <div className="space-y-6">
                            {/* Show preview in the generated link view */}
                            <div className="mb-6">
                              <h5 className="text-sm font-medium text-gray-700 mb-3">
                                Your Payment Preview
                              </h5>

                              {/* Chat-style preview */}
                              <div className="max-w-xs mx-auto">
                                <div
                                  className="rounded-2xl bg-indigo-600 p-4 shadow-md text-white relative"
                                  style={{ maxWidth: "300px" }}
                                >
                                  <div className="absolute top-4 right-4 bg-white/20 rounded-full w-9 h-9 flex items-center justify-center">
                                    <span className="text-white text-lg font-bold">
                                      $
                                    </span>
                                  </div>

                                  <div className="pr-12">
                                    <p className="text-sm opacity-80 break-words">
                                      Pay {savedProfile?.name || "{Your Name}"}{" "}
                                      {formatCurrency(watchAmount || 0)} for
                                      <br />"{watchNote || "Note"}"
                                    </p>
                                    <p className="text-xs opacity-80 mt-2 mb-1">
                                      dollarback.cash
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-md">
                              <h5 className="text-sm font-medium text-gray-900 mb-2">
                                Share your payment link
                              </h5>
                              <div className="grid grid-cols-3 gap-2 mt-4 mb-4">
                                <button
                                  onClick={() => {
                                    if (navigator.share) {
                                      navigator
                                        .share({
                                          title: `Pay ${
                                            savedProfile?.name
                                          } ${formatCurrency(
                                            watchAmount || 0
                                          )}`,
                                          text: `Pay ${
                                            savedProfile?.name
                                          } ${formatCurrency(
                                            watchAmount || 0
                                          )} for "${watchNote}"`,
                                          url: generatedLink,
                                        })
                                        .catch((err) => {
                                          // Don't treat AbortError as a real error - it's just the user canceling
                                          if (err.name !== "AbortError") {
                                            console.error("Share failed:", err);
                                            // Fallback to clipboard if sharing fails for reasons other than user cancellation
                                            navigator.clipboard.writeText(
                                              generatedLink
                                            );
                                            toast.success(
                                              "Link copied to clipboard!"
                                            );
                                          }
                                        });
                                    } else {
                                      navigator.clipboard.writeText(
                                        generatedLink
                                      );
                                      toast.success(
                                        "Link copied to clipboard!"
                                      );
                                    }
                                  }}
                                  className="flex flex-col items-center justify-center p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50"
                                >
                                  <Share2 className="h-6 w-6 text-indigo-600 mb-1" />
                                  <span className="text-xs font-medium">
                                    Share
                                  </span>
                                </button>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      generatedLink
                                    );
                                    toast.success("Link copied to clipboard!");
                                  }}
                                  className="flex flex-col items-center justify-center p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50"
                                >
                                  <Copy className="h-6 w-6 text-indigo-600 mb-1" />
                                  <span className="text-xs font-medium">
                                    Copy
                                  </span>
                                </button>
                                <a
                                  href={`sms:&body=${encodeURIComponent(
                                    `Pay ${savedProfile?.name} ${formatCurrency(
                                      watchAmount || 0
                                    )} for ${watchNote}: ${generatedLink}`
                                  )}`}
                                  className="flex flex-col items-center justify-center p-3 bg-white rounded-md border border-gray-200 hover:bg-gray-50"
                                >
                                  <MessageCircle className="h-6 w-6 text-indigo-600 mb-1" />
                                  <span className="text-xs font-medium">
                                    Text
                                  </span>
                                </a>
                              </div>

                              <h5 className="text-sm font-medium text-gray-900 mb-2 mt-4">
                                What happens next?
                              </h5>
                              <ul className="text-sm text-gray-700 space-y-2">
                                <li className="flex items-start">
                                  <span className="text-indigo-600 mr-2">
                                    1.
                                  </span>
                                  <span>
                                    When someone clicks your link, they'll see
                                    this payment preview
                                  </span>
                                </li>
                                <li className="flex items-start">
                                  <span className="text-indigo-600 mr-2">
                                    2.
                                  </span>
                                  <span>
                                    They'll be redirected to Venmo to complete
                                    the payment
                                  </span>
                                </li>
                                <li className="flex items-start">
                                  <span className="text-indigo-600 mr-2">
                                    3.
                                  </span>
                                  <span>
                                    The payment will be sent directly to your
                                    Venmo account
                                  </span>
                                </li>
                              </ul>
                            </div>

                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={handleClose}
                                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                              >
                                Done
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-red-600">
                            Error generating link.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
